import { useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import LinearProgress from '@mui/material/LinearProgress';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import CheckIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayIcon from '@mui/icons-material/PlayArrow';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useParams, useNavigate } from '@tanstack/react-router';
import { useGetLesson, useGetProgress, useUpdateProgress, useGetSubLessons } from '@onesource/services';
import { LessonProgressStatus, LessonStatus, type TLessonBase } from '@onesource/schemas';
import { LessonsSection } from './lessons-section';

/**
 * Get status display for sub-lesson progress
 */
const getProgressDisplay = (progress?: { status: LessonProgressStatus }) => {
  if (!progress) {
    return {
      icon: <PlayIcon fontSize="small" />,
      label: 'Start',
      color: 'primary' as const,
      variant: 'outlined' as const,
    };
  }

  switch (progress.status) {
    case LessonProgressStatus.COMPLETED: {
      return {
        icon: <CheckIcon fontSize="small" />,
        label: 'Completed',
        color: 'success' as const,
        variant: 'contained' as const,
      };
    }
    case LessonProgressStatus.IN_PROGRESS: {
      return {
        icon: <ScheduleIcon fontSize="small" />,
        label: 'Continue',
        color: 'warning' as const,
        variant: 'contained' as const,
      };
    }
    default: {
      return {
        icon: <PlayIcon fontSize="small" />,
        label: 'Start',
        color: 'primary' as const,
        variant: 'outlined' as const,
      };
    }
  }
};

/**
 * SubLessonCard for displaying sub-lessons in parent lesson view
 */
const SubLessonCard = ({ lesson, index, onClick }: { lesson: TLessonBase; index: number; onClick: () => void }) => {
  const { data: progress } = useGetProgress(lesson.id);
  const progressDisplay = getProgressDisplay(progress || undefined);

  return (
    <Card
      sx={{
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        backgroundColor: 'background.paper',
        '&:hover': {
          boxShadow: 3,
          transform: 'translateY(-2px)',
          borderColor: 'primary.light',
        },
      }}
      elevation={1}
      onClick={onClick}
    >
      <CardContent>
        <Box display="flex" alignItems="flex-start" gap={2}>
          {/* Sub-lesson Number */}
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: 'primary.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 600,
              fontSize: '0.9rem',
              flexShrink: 0,
            }}
          >
            {index + 1}
          </Box>

          {/* Sub-lesson Info */}
          <Box flex={1}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Typography
                variant="h6"
                sx={{
                  flex: 1,
                  fontWeight: 600,
                  fontSize: '1rem',
                }}
              >
                {lesson.title}
              </Typography>
              <Button
                variant={progressDisplay.variant}
                color={progressDisplay.color}
                size="small"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  ml: 2,
                  whiteSpace: 'nowrap',
                }}
              >
                {progressDisplay.label}
              </Button>
            </Box>

            {lesson.shortDescription && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {lesson.shortDescription}
              </Typography>
            )}

            {/* Progress Bar */}
            {progress && progress.status === LessonProgressStatus.IN_PROGRESS && (
              <Box mt={1.5}>
                <LinearProgress
                  variant="determinate"
                  value={progress.progressPercent ?? 50}
                  sx={{ height: 4, borderRadius: 2 }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

/**
 * SubLessonsSection - Shows sub-lessons when viewing a parent lesson
 */
const SubLessonsSection = ({ parentId }: { parentId: string }) => {
  const navigate = useNavigate();
  const { data: subLessons, isLoading } = useGetSubLessons(parentId);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  const publishedSubLessons = subLessons?.filter(l => l.status === LessonStatus.PUBLISHED) || [];

  if (publishedSubLessons.length === 0) {
    return <Alert severity="info">No sub-lessons available yet.</Alert>;
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Sub-Lessons ({publishedSubLessons.length})
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Complete each sub-lesson to finish this section
      </Typography>
      <Stack spacing={2}>
        {publishedSubLessons.map((subLesson, index) => (
          <SubLessonCard
            key={subLesson.id}
            lesson={subLesson}
            index={index}
            onClick={() => navigate({ to: '/community-hub/lessons/$id', params: { id: subLesson.id } })}
          />
        ))}
      </Stack>
    </Box>
  );
};

/**
 * Lesson Detail Page
 * Shows a lesson with its content items in a clean layout
 * Supports both parent lessons (with sub-lessons) and regular lessons (with content blocks)
 */
export const LessonDetailPage = () => {
  const { id } = useParams({ from: '/_protected/community-hub/lessons/$id' });
  const navigate = useNavigate();
  const { data: lesson, isLoading, error } = useGetLesson(id);
  const { data: progress } = useGetProgress(id);
  const updateProgress = useUpdateProgress(id);

  // Fetch parent lesson if this is a sub-lesson
  const { data: parentLesson } = useGetLesson(lesson?.parentId || '', {
    enabled: !!lesson?.parentId,
  });

  // Check if this lesson has sub-lessons
  const hasSubLessons = (lesson?.subLessonCount || 0) > 0 || (lesson?.children && lesson.children.length > 0);
  const isSubLesson = !!lesson?.parentId;

  // Mark lesson as in progress when viewing (only for lessons without sub-lessons)
  useEffect(() => {
    if (lesson && !hasSubLessons && (!progress || progress.status === LessonProgressStatus.NOT_STARTED)) {
      updateProgress.mutate({ status: LessonProgressStatus.IN_PROGRESS });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only trigger once when lesson loads
  }, [lesson?.id, hasSubLessons]);

  // Handle mark as complete
  const handleMarkComplete = () => {
    updateProgress.mutate({ status: LessonProgressStatus.COMPLETED });
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load lesson: {error.message}</Alert>
      </Box>
    );
  }

  if (!lesson) {
    return (
      <Box p={3}>
        <Alert severity="error">Lesson not found</Alert>
      </Box>
    );
  }

  // Sort content blocks by position
  const sortedBlocks = [...(lesson.contentBlocks || [])].sort((a, b) => a.position - b.position);

  const isCompleted = progress?.status === LessonProgressStatus.COMPLETED;

  return (
    <Box p={{ xs: 2, md: 3 }} maxWidth="1200px" mx="auto">
      {/* Breadcrumbs for sub-lessons */}
      {isSubLesson && parentLesson && (
        <Box mb={2}>
          <Breadcrumbs>
            <Link
              component="button"
              underline="hover"
              color="inherit"
              onClick={() => navigate({ to: '/community-hub/lessons/$id', params: { id: parentLesson.id } })}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
            >
              <ArrowBackIcon fontSize="small" />
              {parentLesson.title}
            </Link>
            <Typography color="text.primary">{lesson.title}</Typography>
          </Breadcrumbs>
        </Box>
      )}

      {/* Lesson Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box flex={1}>
          <Box display="flex" alignItems="center" gap={1.5} mb={1}>
            <Typography variant="h4">{lesson.title}</Typography>
            {isSubLesson && <Chip label="Sub-lesson" size="small" variant="outlined" color="secondary" />}
            {hasSubLessons && (
              <Chip
                label={`${lesson.subLessonCount || lesson.children?.length || 0} sub-lessons`}
                size="small"
                color="secondary"
                variant="filled"
              />
            )}
          </Box>
          {lesson.shortDescription && (
            <Typography variant="body1" color="text.secondary">
              {lesson.shortDescription}
            </Typography>
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={2} flexShrink={0}>
          {/* Only show complete button for lessons without sub-lessons */}
          {!hasSubLessons &&
            (isCompleted ? (
              <Chip icon={<CheckIcon />} label="Completed" color="success" variant="filled" />
            ) : (
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckIcon />}
                onClick={handleMarkComplete}
                disabled={updateProgress.isPending}
              >
                Mark Complete
              </Button>
            ))}
        </Box>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Long Description (if available) */}
      {lesson.longDescription && (
        <Box mb={4}>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {lesson.longDescription}
          </Typography>
        </Box>
      )}

      {/* Show Sub-Lessons OR Content Blocks */}
      {hasSubLessons ? (
        <SubLessonsSection parentId={id} />
      ) : sortedBlocks.length === 0 ? (
        <Alert severity="info">This lesson has no content yet.</Alert>
      ) : (
        <LessonsSection contentBlocks={sortedBlocks} lessonId={id} />
      )}
    </Box>
  );
};
